import {
  Activity,
  AlertTriangle,
  Calendar,
  CheckCircle,
  Code,
  FileText,
  Info,
  Layers,
  Link as LinkIcon,
  List,
  Shield,
  Sigma,
  UserCheck,
  Users,
  UserX,
} from "lucide-react";
import { useTranslation } from "react-i18next";
import { cn } from "@/lib/utils";
import { renderMarkdown } from "@/lib/render-markdown";

const TILE_ICONS: Record<string, typeof Users> = {
  users: Users,
  shield: Shield,
  link: LinkIcon,
  activity: Activity,
  "user-check": UserCheck,
  "user-x": UserX,
  layers: Layers,
  code: Code,
  "check-circle": CheckCircle,
  "alert-triangle": AlertTriangle,
  list: List,
  "file-text": FileText,
  calendar: Calendar,
  sigma: Sigma,
};

export interface KvRow {
  k: string;
  v: React.ReactNode;
  mono?: boolean;
}

export interface TableSpec {
  cols: string[];
  rows: React.ReactNode[][];
  footer?: React.ReactNode[];
}

export interface TilePayload {
  ovl: string;
  big: React.ReactNode;
  small?: React.ReactNode;
  icon?: string;
  accent?: boolean;
}

export interface BarPayload {
  lbl: string;
  n: React.ReactNode;
  /**
   * Fração calculada ou `undefined` (não medida). 
   * Tipo obrigatório para forçar a declaração do estado (`0` é um valor válido e não indica ausência).
   */
  pct: number | undefined;
  color: string;
}

export type SeverityLevel = "crit" | "warn" | "ok" | "info";

export interface RowPayload {
  sev: SeverityLevel;
  l: React.ReactNode;
  r: React.ReactNode;
  x: React.ReactNode;
}

export type LogLevel = "I" | "W" | "E";

export interface LogLine {
  ts: string;
  lvl: LogLevel;
  tx: React.ReactNode;
}

interface BasePayload {
  title: string;
  desc?: string;
  placeholder?: boolean;
}

export type RendererPayload =
  | (BasePayload & { kind: "kv"; rows: KvRow[]; table?: TableSpec; tableTitle?: string })
  | (BasePayload & {
      kind: "tiles";
      tiles: TilePayload[];
      table?: TableSpec;
      tableTitle?: string;
    })
  | (BasePayload & {
      kind: "tiles+table";
      tiles: TilePayload[];
      table?: TableSpec;
      tableTitle?: string;
    })
  | (BasePayload & {
      kind: "bars";
      pillP?: string;
      bars: BarPayload[];
      decisionsTitle?: string;
      decisionsBody?: React.ReactNode;
    })
  | (BasePayload & { kind: "rows"; rows: RowPayload[] })
  | (BasePayload & { kind: "logs"; lines: LogLine[] })
  | (BasePayload & { kind: "table"; table: TableSpec })
  | (BasePayload & {
      kind: "note+table";
      note?: React.ReactNode;
      table: TableSpec;
    })
  | (BasePayload & { kind: "markdown"; md: string });

function DataHead({ title, desc, pill }: { title: React.ReactNode; desc?: string; pill?: React.ReactNode }) {
  return (
    <div className="mb-3.5">
      <h3 className="font-brand font-bold text-base text-[#08254c] m-0 mb-1 tracking-[-0.01em] flex items-center gap-2.5">
        {title}
        {pill}
      </h3>
      {desc && <p className="text-xs text-[#7b8799] m-0 leading-relaxed">{desc}</p>}
    </div>
  );
}

function PlaceholderBanner({ title }: { title: string }) {
  return (
    <div className="rounded-lg border border-dashed border-[#e0e6ed] bg-[#fafbfc] px-5 py-8 text-center">
      <Info className="w-4 h-4 text-[#b2bccb] mx-auto mb-2" />
      <p className="font-mono text-[10px] tracking-[0.18em] uppercase text-[#7b8799] m-0">
        {title}
      </p>
    </div>
  );
}

// S-020: skeleton mostrado quando a run completou mas o detail do
// output_summary (~19MB via /executions/{id}) ainda está sendo fetched.
// Diferente do PlaceholderBanner ("aguardando o nó upstream rodar") — aqui
// já SABEMOS que tem dado, é só o transit. Visual blocks pulsantes pra
// indicar "loading", não "vazio".
function SkeletonBlocks() {
  return (
    <div className="space-y-3" aria-busy="true" aria-live="polite">
      <div className="grid gap-2.5 grid-cols-[repeat(auto-fit,minmax(180px,1fr))]">
        {[0, 1, 2].map((i) => (
          <div
            key={i}
            className="bg-white border border-[#e8ebf0] rounded-lg p-3.5 h-[88px] animate-pulse"
          >
            <div className="h-2 bg-[#e8ebf0] rounded w-1/2 mb-2.5" />
            <div className="h-5 bg-[#e8ebf0] rounded w-2/3 mb-1.5" />
            <div className="h-2 bg-[#f2f4f7] rounded w-1/3" />
          </div>
        ))}
      </div>
      <div className="border border-[#e8ebf0] rounded-lg overflow-hidden bg-white">
        {[0, 1, 2, 3].map((i) => (
          <div
            key={i}
            className="grid grid-cols-[160px_1fr] border-b border-[#f2f4f7] last:border-b-0 px-3.5 py-2.5 animate-pulse"
          >
            <div className="h-2 bg-[#e8ebf0] rounded w-2/3" />
            <div className="h-2 bg-[#f2f4f7] rounded w-1/2" />
          </div>
        ))}
      </div>
    </div>
  );
}

function RTable({ table, title }: { table: TableSpec; title?: string }) {
  return (
    <div className="mt-3.5 border border-[#e8ebf0] rounded-lg overflow-hidden bg-white">
      {title && (
        <div className="px-3.5 py-2 font-mono text-[9px] text-[#596b85] tracking-[0.18em] uppercase font-semibold bg-[#fafbfc] border-b border-[#e8ebf0]">
          {title}
        </div>
      )}
      <table className="w-full border-collapse text-[11.5px]">
        <thead>
          <tr>
            {table.cols.map((c, i) => (
              <th
                key={i}
                className="text-left px-3 py-2 font-mono text-[9px] text-[#596b85] tracking-[0.1em] uppercase font-semibold bg-[#fafbfc] border-b border-[#e8ebf0]"
              >
                {c}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {table.rows.map((row, i) => (
            <tr key={i}>
              {row.map((cell, j) => (
                <td
                  key={j}
                  className={cn(
                    "px-3 py-2 border-b border-[#f2f4f7] text-[#08254c] last:border-b-0",
                    j === 0 && "font-mono text-[11px] font-semibold",
                  )}
                >
                  {cell}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
        {table.footer && (
          <tfoot>
            <tr>
              {table.footer.map((cell, i) => (
                <td
                  key={i}
                  className="px-3 py-2 font-bold bg-[#fafbfc] border-t-[1.5px] border-[#08254c] text-[#08254c]"
                >
                  {cell}
                </td>
              ))}
            </tr>
          </tfoot>
        )}
      </table>
    </div>
  );
}

function RKV({ d }: { d: Extract<RendererPayload, { kind: "kv" }> }) {
  if (d.placeholder) return <PlaceholderRoot d={d} />;
  return (
    <>
      <DataHead title={d.title} desc={d.desc} />
      <div className="grid grid-cols-1 border border-[#e8ebf0] rounded-lg overflow-hidden bg-white">
        {d.rows.map((r, i) => (
          <div
            key={i}
            className="grid grid-cols-[200px_1fr] border-b border-[#f2f4f7] last:border-b-0"
          >
            <div className="px-3.5 py-2.5 bg-[#fafbfc] font-mono text-[10px] text-[#596b85] tracking-[0.06em] uppercase font-semibold border-r border-[#f2f4f7]">
              {r.k}
            </div>
            <div
              className={cn(
                "px-3.5 py-2.5 text-xs text-[#08254c] leading-relaxed",
                r.mono && "font-mono text-[11px]",
              )}
            >
              {r.v}
            </div>
          </div>
        ))}
      </div>
      {d.table && <RTable table={d.table} title={d.tableTitle} />}
    </>
  );
}

function TileIcon({ name }: { name?: string }) {
  if (!name) return null;
  const Icon = TILE_ICONS[name] ?? Info;
  return (
    <div className="absolute top-3 right-3 w-[26px] h-[26px] rounded-md bg-[rgba(255,200,61,0.12)] flex items-center justify-center text-[#b57a00]">
      <Icon className="w-3.5 h-3.5" />
    </div>
  );
}

function RTiles({ d }: { d: Extract<RendererPayload, { kind: "tiles" | "tiles+table" }> }) {
  if (d.placeholder) return <PlaceholderRoot d={d} />;
  return (
    <>
      <DataHead title={d.title} desc={d.desc} />
      <div className="grid gap-2.5 mb-3.5 grid-cols-[repeat(auto-fit,minmax(180px,1fr))]">
        {d.tiles.map((t, i) => (
          <div
            key={i}
            className={cn(
              "bg-white border border-[#e8ebf0] rounded-lg p-3.5 relative overflow-hidden",
              t.accent &&
                "border-[rgba(255,200,61,0.5)] bg-[linear-gradient(135deg,rgba(255,200,61,0.06),rgba(255,200,61,0.01))]",
            )}
          >
            <TileIcon name={t.icon} />
            <div className="font-mono text-[9px] text-[#7b8799] tracking-[0.18em] uppercase font-semibold mb-1.5">
              {t.ovl}
            </div>
            <div className="font-brand font-extrabold text-2xl text-[#08254c] tracking-[-0.02em] leading-none mb-1.5">
              {t.big}
            </div>
            {t.small && <div className="text-[11px] text-[#7b8799] leading-snug">{t.small}</div>}
          </div>
        ))}
      </div>
      {d.table && <RTable table={d.table} title={d.tableTitle} />}
    </>
  );
}

function RBars({ d }: { d: Extract<RendererPayload, { kind: "bars" }> }) {
  const { t } = useTranslation();
  if (d.placeholder) return <PlaceholderRoot d={d} />;
  return (
    <>
      <DataHead
        title={d.title}
        desc={d.desc}
        pill={
          d.pillP && (
            <span className="font-mono text-[9px] font-semibold px-1.5 py-0.5 rounded-full bg-[rgba(39,161,85,0.1)] text-[#27a155] border border-[rgba(39,161,85,0.25)] tracking-[0.08em] uppercase">
              {t("{{percent}} complete", { percent: d.pillP })}
            </span>
          )
        }
      />
      <div className="flex flex-col gap-2 mb-3.5">
        {d.bars.map((b, i) => (
          <div key={i} className="grid grid-cols-[96px_1fr_60px_50px] gap-3 items-center">
            <div className="font-brand font-bold text-[11px] text-[#08254c] flex items-center gap-1.5">
              <span
                className="w-2.5 h-2.5 rounded-sm flex-shrink-0"
                style={{ background: b.color }}
              />
              {b.lbl}
            </div>
            {/* Sem fração não há trilho: uma barra vazia lê-se como zero, e a
                distinção entre "não medi" e "deu zero" é justamente o ponto. */}
            <div className="relative h-4 bg-[#f2f4f7] rounded-[3px] overflow-hidden">
              {b.pct != null && (
                <div
                  className="h-full rounded-[3px]"
                  style={{
                    width: `${b.pct}%`,
                    background: `linear-gradient(90deg, ${b.color}, ${b.color}aa)`,
                  }}
                />
              )}
            </div>
            <div className="font-mono text-[11px] text-[#08254c] font-semibold text-right">
              {b.n}
            </div>
            <div className="font-mono text-[10px] text-[#7b8799] text-right">
              {b.pct != null ? `${b.pct}%` : "—"}
            </div>
          </div>
        ))}
      </div>
      {d.decisionsTitle && (
        <div className="mt-3.5 px-3.5 py-3 bg-[#fafbfc] border border-[#e8ebf0] rounded-lg border-l-[3px] border-l-[#ffc83d]">
          <div className="font-mono text-[9px] text-[#596b85] tracking-[0.18em] uppercase font-semibold mb-1">
            {d.decisionsTitle}
          </div>
          <div className="text-[11.5px] text-[#08254c] leading-relaxed">{d.decisionsBody}</div>
        </div>
      )}
    </>
  );
}

function severityColor(sev: SeverityLevel): string {
  switch (sev) {
    case "crit":
      return "#dc2626";
    case "warn":
      return "#f27325";
    case "ok":
      return "#27a155";
    case "info":
    default:
      return "#1d4ea0";
  }
}

function RRows({ d }: { d: Extract<RendererPayload, { kind: "rows" }> }) {
  if (d.placeholder) return <PlaceholderRoot d={d} />;
  return (
    <>
      <DataHead title={d.title} desc={d.desc} />
      <div className="flex flex-col gap-1.5">
        {d.rows.map((r, i) => (
          <div
            key={i}
            className="flex gap-2.5 px-3.5 py-3 bg-white border border-[#e8ebf0] rounded-lg"
          >
            <span
              className="w-2 h-2 rounded-full flex-shrink-0 mt-1.5"
              style={{ background: severityColor(r.sev) }}
            />
            <div className="flex-1 min-w-0">
              <div className="flex justify-between items-baseline gap-2.5 mb-1">
                <span className="font-brand font-bold text-[13px] text-[#08254c] tracking-[-0.005em]">
                  {r.l}
                </span>
                <span className="font-mono text-[11px] text-[#b57a00] font-semibold">{r.r}</span>
              </div>
              <div className="text-[11.5px] text-[#596b85] leading-relaxed">{r.x}</div>
            </div>
          </div>
        ))}
      </div>
    </>
  );
}

function RLogs({ d }: { d: Extract<RendererPayload, { kind: "logs" }> }) {
  if (d.placeholder) return <PlaceholderRoot d={d} />;
  return (
    <>
      <DataHead title={d.title} desc={d.desc} />
      <div className="bg-[#0d1b2e] rounded-lg px-3.5 py-3 font-mono text-[11px] leading-relaxed text-white/85">
        {d.lines.map((l, i) => (
          <div
            key={i}
            className="grid grid-cols-[90px_18px_1fr] gap-2 py-px"
          >
            <span className="text-white/35">{l.ts}</span>
            <span
              className={cn(
                "text-center",
                l.lvl === "W" && "text-[#ffc83d]",
                l.lvl === "E" && "text-[#ff7575]",
                l.lvl === "I" && "text-[#7eb8ff]",
              )}
            >
              {l.lvl}
            </span>
            <span className="text-white/85">{l.tx}</span>
          </div>
        ))}
      </div>
    </>
  );
}

function RNoteTable({ d }: { d: Extract<RendererPayload, { kind: "note+table" }> }) {
  if (d.placeholder) return <PlaceholderRoot d={d} />;
  return (
    <>
      <DataHead title={d.title} desc={d.desc} />
      {d.note && (
        <div className="flex gap-2 items-start px-3.5 py-2.5 bg-[#fffaea] border border-[rgba(255,200,61,0.45)] rounded-lg mb-3.5 text-[11.5px] text-[#6a5008] leading-relaxed">
          <Info className="w-3.5 h-3.5 text-[#b57a00] flex-shrink-0 mt-0.5" />
          <span>{d.note}</span>
        </div>
      )}
      <RTable table={d.table} />
    </>
  );
}

function RMarkdown({ d }: { d: Extract<RendererPayload, { kind: "markdown" }> }) {
  if (d.placeholder) return <PlaceholderRoot d={d} />;
  return (
    <>
      <DataHead title={d.title} desc={d.desc} />
      <div className="px-5 py-4 bg-white border border-[#e8ebf0] rounded-lg text-[#08254c] leading-relaxed text-[12.5px] sku-md">
        {renderMarkdown(d.md)}
      </div>
    </>
  );
}

function PlaceholderRoot({ d, loading }: { d: RendererPayload; loading?: boolean }) {
  const { t } = useTranslation();
  return (
    <>
      <DataHead title={d.title} desc={loading ? t("Loading run data…") : d.desc} />
      {loading ? <SkeletonBlocks /> : <PlaceholderBanner title={d.desc ?? t("Awaiting node output")} />}
    </>
  );
}

export function NodeData({
  data,
  loading = false,
}: {
  data: RendererPayload | null | undefined;
  loading?: boolean;
}) {
  const { t } = useTranslation();
  if (!data) {
    if (loading) {
      // Sem payload nem placeholder, mas a run completed existe e o detail
      // está sendo fetched — mostra skeleton em vez do "No data".
      return (
        <div className="py-4">
          <SkeletonBlocks />
        </div>
      );
    }
    return (
      <div className="px-10 py-10 text-[#7b8799] text-sm">{t("No data for this tab yet.")}</div>
    );
  }
  // S-020: payload vem como placeholder quando outputs está null. Se o detail
  // está sendo carregado (loading=true), o skeleton transmite "tá vindo" em
  // vez de "vazio". Quando loading=false, mantém o PlaceholderBanner clássico
  // (significa "nó upstream ainda não rodou").
  if (data.placeholder) {
    return <PlaceholderRoot d={data} loading={loading} />;
  }
  switch (data.kind) {
    case "kv":
      return <RKV d={data} />;
    case "tiles":
    case "tiles+table":
      return <RTiles d={data} />;
    case "bars":
      return <RBars d={data} />;
    case "rows":
      return <RRows d={data} />;
    case "logs":
      return <RLogs d={data} />;
    case "table":
      return (
        <>
          <DataHead title={data.title} desc={data.desc} />
          <RTable table={data.table} />
        </>
      );
    case "note+table":
      return <RNoteTable d={data} />;
    case "markdown":
      return <RMarkdown d={data} />;
    default:
      return null;
  }
}
